<template>
  <div class="pt-3">
    <el-form :model="field.price_settings" :rules="priceRules" ref="priceForm">
      <!-- Title and Description -->
      <title-and-description :field="field" />

      <!-- 🔵 Step 1: Choose Pricing Type -->
      <el-form-item>
        <label
          class="field-title-label d-block w-100 text-lightGray text-xs 2xl:!text-sm font-normal !mb-1"
        >
          Pricing Type
        </label>
        <el-radio-group v-model="field.price_settings.pricingType">
          <el-radio label="step_price">Step Pricing (Duration Based)</el-radio>
          <el-radio label="fixed_price">Fixed Pricing (Hour/Day/Week/Month/Year)</el-radio>
        </el-radio-group>
      </el-form-item>

      <!-- 🔵 Step 2: Based on selection, show fields -->

      <!-- ✅ Step Pricing Fields -->
      <div v-if="field.price_settings.pricingType === 'step_price'">
        <el-row :gutter="20" type="flex" align="middle">
          <el-col :span="10">
            <el-form-item label="Configuration Mode" size="mini" prop="configMode">
              <el-select
                v-model="field.price_settings.configMode"
                placeholder="Select configuration mode"
                style="width: 100%"
                size="mini"
                clearable
              >
                <el-option label="Configure" value="configure" />
                <el-option label="Take Configuration From" value="reference" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="10" style="margin-left: 0%">
            <el-form-item
              v-if="field.price_settings.configMode === 'reference'"
              label="Take Configuration From"
              size="mini"
              prop="referenceField"
            >
              <el-select
                v-model="field.price_settings.referenceField"
                placeholder="Select price field"
                style="width: 100%"
                size="mini"
                clearable
              >
                <el-option
                  v-for="template in priceFieldsOnly"
                  :key="template._id"
                  :label="template.label"
                  :value="template.key"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <price-slabs-tabs
          v-if="field.price_settings.configMode === 'configure'"
          ref="priceSlabs"
          :field="field"
        />

        <!-- Duration Field and Threshold Value -->
        <div class="duration_field ml-0">
          <el-row :gutter="20" type="flex" align="">
            <el-col :span="12">
              <el-form-item label="Select the Field for Duration" size="mini" prop="duration">
                <el-select
                  v-model="field.price_settings.duration"
                  placeholder="Select field from template"
                  style="width: 100%"
                  filterable
                  clearable
                  default-first-option
                  size="mini"
                  :disabled="isManualInput('duration')"
                >
                  <el-option
                    v-for="template in excludeCurrentField"
                    :key="template._id"
                    :label="template.label"
                    :value="template.key"
                  />
                </el-select>
                <div class="note py-3">OR</div>
                <el-input
                  :value="isManualInput('duration') ? field.price_settings.duration : ''"
                  type="number"
                  placeholder="Enter fixed value"
                  size="mini"
                  style="width: 100%; margin-top: 2px"
                  :disabled="isSelectInput('duration')"
                  @input="(val) => onManualInputChange(val, 'duration')"
                />
              </el-form-item>
              <div class="note text-[#F2994A]">
                <b class="font-medium">Note</b>
                : field will be used to calculate price based on selected value (Minutes).
              </div>
            </el-col>

            <el-col :span="12">
              <el-form-item label="Threshold Price" size="mini" prop="thresholdValue">
                <el-select
                  v-model="field.price_settings.thresholdValue"
                  placeholder="Select field from template"
                  style="width: 100%"
                  filterable
                  clearable
                  default-first-option
                  size="mini"
                  :disabled="isManualInput('thresholdValue')"
                >
                  <el-option
                    v-for="template in excludeCurrentField"
                    :key="template._id"
                    :label="template.label"
                    :value="template.key"
                  />
                </el-select>
                <div class="note py-3">OR</div>
                <el-input
                  :value="
                    isManualInput('thresholdValue') ? field.price_settings.thresholdValue : ''
                  "
                  type="number"
                  placeholder="Enter fixed value"
                  size="mini"
                  style="width: 100%; margin-top: 2px"
                  :disabled="isSelectInput('thresholdValue')"
                  @input="(val) => onManualInputChange(val, 'thresholdValue')"
                />
              </el-form-item>
              <div class="note text-[#F2994A]">
                <b class="font-medium">Note</b>
                : range reaches maximum, we add this threshold value.
              </div>
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- ✅ Fixed Pricing Fields -->
      <div v-if="field.price_settings.pricingType === 'fixed_price'">
        <el-row :gutter="20" type="flex" align="top" style="flex-wrap: wrap; margin-left: 0px">
          <el-col v-for="(label, key) in priceFields" :key="key" :span="8">
            <el-form-item :label="label" size="mini" class="!mb-0" :prop="key">
              <template #label>
                {{ label }}
              </template>
              <el-select
                v-model="field.price_settings[key]"
                placeholder="Select field from template"
                style="width: 100%"
                filterable
                clearable
                default-first-option
                size="mini"
                :disabled="isManualInput(key)"
              >
                <el-option
                  v-for="template in excludeCurrentField"
                  :key="template._id"
                  :label="template.label"
                  :value="template.key"
                />
              </el-select>
              <div class="note py-3">OR</div>

              <el-input
                :value="isManualInput(key) ? field.price_settings[key] : ''"
                type="number"
                placeholder="Enter fixed value"
                size="mini"
                style="width: 100%; margin-top: 2px"
                :disabled="isSelectInput(key)"
                @input="(val) => onManualInputChange(val, key)"
              />
            </el-form-item>
            <div class="note text-[#F2994A]">
              <b class="font-medium">Note</b>
              : Select field or {{ label }}.
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- Currency + Allow User to Configure -->
      <div style="margin-top: 20px; margin-left: 0px">
        <el-row :gutter="20" type="flex" align="middle">
          <!-- <el-form> -->
          <el-col :span="10">
            <el-form-item size="mini" prop="currency">
              <label
                class="field-title-label d-block w-100 text-lightGray text-xs 2xl:!text-sm font-normal !mb-1"
              >
                Select the Currency
              </label>
              <el-select
                v-model="field.validations.currency"
                filterable
                @change="currencyVariable"
                class="select-input w-100 mb-1"
              >
                <el-option
                  v-for="item in CurrencyTypes"
                  :key="item.value"
                  :label="item.value"
                  :value="item.value"
                >
                  <span style="float: left">{{ item.value }}</span>
                  <span
                    style="float: right; color: var(--el-text-color-secondary); font-size: 13px"
                  >
                    {{ item.name }}
                  </span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="10" v-if="field.price_settings.pricingType === 'step_price'">
            <el-form-item>
              <el-checkbox v-model="field.properties.allow_user_to_configure">
                <span class="!text-brand-500">Allow User to Configure</span>
              </el-checkbox>
            </el-form-item>
          </el-col>
          <!-- </el-form> -->
        </el-row>
      </div>
    </el-form>
  </div>
</template>

<script>
  //import TitleAndDescription from "./TitleAndDescription";
  export default {
    components: {
      TitleAndDescription: () => import('./TitleAndDescription'),
      PriceSlabsTabs: () => import('../PriceSlabsTabs.vue'),
    },
    props: ['field', 'templatesData', 'selfTemplate', 'selfTemplateId', 'isFromDocument'],

    data() {
      return {
        activeName: 'uniform',
        uniform: {
          steps: null,
          interval: null,
          slabs: [],
        },
        nonUniform: {
          steps: null,
          interval: null,
          slabs: [],
        },
        stepError: '',
        intervalError: '',
        priceRules: {
          duration: [
            {
              required: true,
              message: 'Please select a duration field',
              trigger: 'change',
            },
          ],
          thresholdValue: [
            {
              required: true,
              message: 'Please enter a threshold value',
              trigger: 'blur',
            },
          ],
        },
        priceFields: {
          hourly_price: 'Hourly Price',
          daily_price: 'Daily Price',
          weekly_price: 'Weekly Price',
          monthly_price: 'Monthly Price',
          yearly_price: 'Yearly Price',
          total_days: 'Total Number of Days',
        },
        CurrencyTypes: [
          { value: 'USD', name: '$' },
          { value: 'CAD', name: '$' },
          { value: 'AUD', name: '$' },
          { value: 'INR', name: '₹' },
          { value: 'EUR', name: '€' },
          { value: 'MXN', name: '$' },
          { value: 'ZAR', name: 'R' },
          { value: 'MYR', name: 'RM' },
          { value: 'GBP', name: '£' },
          { value: 'AED', name: 'د.إ' },
        ],
      };
    },
    mounted() {
      // Set default field.price_settings if not present
      if (!this.field.price_settings) {
        this.$set(this.field, 'price_settings', {
          pricingType: '',
          configMode: '',
          hourly_price: '',
          daily_price: '',
          weekly_price: '',
          monthly_price: '',
          yearly_price: '',
          total_days: '',
          activeName: 'uniform',
          isUniform: true,
          maxSteps: '',
          intervalMin: '',
          pricingSlabs: [],
        });
      }
      // Sync from field to local (optional if loading from saved data)
      if (this.field.price_settings && this.field.price_settings.pricingType === 'step_price') {
        this.activeName = this.field.price_settings?.activeName || 'uniform';
        this.uniform.steps = this.field.price_settings.maxSteps || null;
        this.uniform.interval = this.field.price_settings.intervalMin || null;

        if (this.field.price_settings.isUniform) {
          this.uniform.slabs = this.field.price_settings.pricingSlabs || [];
        } else {
          this.nonUniform.slabs = this.field.price_settings.pricingSlabs || [];
        }
        this.field.price_settings.thresholdValue = this.field.price_settings?.thresholdValue;
      }
      if (this.field.price_settings && this.field.price_settings.pricingType === 'fixed_price') {
        this.field.price_settings.hourly_price = this.field.price_settings?.hourly_price || null;
        this.field.price_settings.daily_price = this.field.price_settings?.daily_price || null;
        this.field.price_settings.weekly_price = this.field.price_settings?.weekly_price || null;
        this.field.price_settings.monthly_price = this.field.price_settings?.monthly_price || null;
        this.field.price_settings.yearly_price = this.field.price_settings?.yearly_price || null;
        this.field.price_settings.total_days = this.field.price_settings?.total_days || null;
      }
    },
    computed: {
      excludeCurrentField() {
        let inputTypes = ['NUMBER', 'FORMULA', 'CURRENCY'];
        let filteredFields = this.selfTemplate.filter(
          (field) => this.field.key !== field.key && inputTypes.includes(field.inputType)
        );
        return filteredFields;
      },
      priceFieldsOnly() {
        let inputTypes = ['PRICE'];
        //return this.templatesData.filter((field) => field.inputType === "PRICE");
        let filteredFields = this.selfTemplate.filter(
          (field) => this.field.key !== field.key && inputTypes.includes(field.inputType)
        );
        return filteredFields;
      },
    },
    watch: {
      'field.price_settings.pricingType': {
        handler(newVal) {
          if (newVal === 'step_price' && !this.field.price_settings.configMode) {
            this.$set(this.field.price_settings, 'configMode', 'configure');
          }
        },
        immediate: true, // to run on initial mount too
      },
    },

    methods: {
      validatePriceForm() {
        this.$refs.priceForm.validate((valid) => {
          if (valid) {
            console.log('Form valid!');
          } else {
            console.warn('Form has errors!');
          }
        });
      },
      currencyVariable() {
        const currencyLocaleMap = {
          INR: 'en-IN',
          USD: 'en-US',
          CAD: 'en-CA',
          MXN: 'es-MX',
          ZAR: 'en-ZA',
          MYR: 'ms-MY',
          GBP: 'en-GB',
          AED: 'ar-AED',
        };
        this.field.validations.locale =
          currencyLocaleMap[this.field.validations.currency] || 'en-IE';
      },
      isManualInput(key) {
        const val = this.field.price_settings[key];
        return typeof val === 'number' && !isNaN(val);
      },
      isSelectInput(key) {
        const val = this.field.price_settings[key];
        return typeof val === 'string' && val !== '';
      },
      onManualInputChange(val, key) {
        if (val === '' || val === null) {
          this.$set(this.field.price_settings, key, '');
        } else {
          const num = Number(val);
          if (!isNaN(num)) {
            this.$set(this.field.price_settings, key, num);
          }
        }
      },
    },
  };
</script>

<style scoped>
  .demo-tabs > .el-tabs__content {
    padding: 26px;
    color: #6b778c;
    font-size: 32px;
    font-weight: 600;
  }

  .error {
    font-size: 12px;
    color: red;
    margin-top: 4px;
  }

  .duration_field {
    height: 32px;
    /* width: 90%; */
    margin-left: 3%;
    margin-bottom: 17%;
  }

  .required {
    color: red;
  }
</style>
